import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet } from '@tanstack/react-router';
import { api } from './lib/api';
import { queryKeys } from './lib/query';
import { brandLogoUrl } from './lib/branding';
import { useSession } from './auth/AuthGate';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { ThemeToggle } from './components/ThemeToggle';
import { ProviderModeBanner } from './components/ProviderModeBanner';

/**
 * Powloka aplikacji: nawigacja + `Outlet`.
 *
 * Cala powloka zyje za sesja (`AuthGate` montuje ja dopiero po `GET /api/me`),
 * dlatego branding i rola sa tu pewne, a nie opcjonalne. Ekran zdrowia
 * bindingow, ktory byl tu w M0, przeniosl sie do `HomePage`.
 */
export function App() {
  const session = useSession();
  const queryClient = useQueryClient();
  const logo = brandLogoUrl(session.branding);
  const productName = session.branding?.productName ?? 'GenAI Studio';

  const logout = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      queryClient.clear();
    },
  });

  return (
    <div className="min-h-full">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-1 focus:text-sm dark:focus:bg-neutral-900"
      >
        Przejdz do tresci
      </a>

      <ProviderModeBanner />

      <header className="border-b border-neutral-200 dark:border-neutral-800">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 p-3">
          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            {logo === null ? null : <img src={logo} alt="" className="h-6" />}
            {productName}
          </Link>

          <nav aria-label="Glowna" className="flex flex-wrap items-center gap-1 text-sm">
            <NavLink to="/clients" label="Katalog" />
            <NavLink to="/gallery" label="Galeria" />
            <NavLink to="/settings" label="Ustawienia" />
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <Badge tone="info">{session.role ?? 'brak roli'}</Badge>
            <span className="hidden text-xs text-neutral-500 sm:inline">{session.user.email}</span>
            <ThemeToggle />
            <Button size="sm" variant="outline" onClick={() => logout.mutate()}>
              Wyloguj
            </Button>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-7xl p-4">
        <Outlet />
      </main>
    </div>
  );
}

function NavLink({ to, label }: { readonly to: '/clients' | '/gallery' | '/settings'; readonly label: string }) {
  return (
    <Link
      to={to}
      className="rounded px-2 py-1 text-neutral-600 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] dark:text-neutral-300 dark:hover:bg-neutral-800"
      activeProps={{ className: 'rounded px-2 py-1 font-medium text-neutral-900 dark:text-neutral-100' }}
    >
      {label}
    </Link>
  );
}

export default App;
